/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useRef, useContext, useState } from 'react';
import { Config, EditableComponent, MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { LocalHeaderProps } from './LocalHeader.types';
import { StyledLocalHeader } from './LocalHeader.styles';

import { LocalHeaderDescription } from './components/LocalHeaderDescription';
import { LocalHeaderNavigation } from './components/LocalHeaderNavigation';

import { PageParamsContext } from '../../modules/context/PageContext';
import { cna } from '../../modules/utils/constants/constants';
import { LocalHeaderData } from './data/LocalHeaderData';

const { NEXT_PUBLIC_AEM_SITE } = process.env;

export const LocalHeader: FC<LocalHeaderProps> = pageProps => {
  let offsetTop = 0;
  let lastScrollTop = 0;
  let globalNavHeight = 0;
  const linkedTargetElements: Array<{
    targetElement: Element | null;
    linkElement: null;
  }> = [];
  const [hideMap, setHideMap] = useState<boolean>(false);
  const [hideNavigation, setHideNavigation] = useState<boolean>(false);
  const singleRefLocalheaderTop = useRef<HTMLDivElement>(null);
  const singleRefAnchorList = useRef<HTMLDivElement>(null);
  const [localHeaderData, setLocalHeaderData] = useState(null);

  const { preProcessorResponse } = useContext(PageParamsContext);

  useEffect(() => {
    document.querySelector('.global-nav')?.classList.add('page-wrap');
    if (pageProps?.hideMap) {
      hideFunction(pageProps?.selectedStatesMap, setHideMap);
    }
    if (pageProps?.hideL2Navigation) {
      hideFunction(pageProps?.selectedStatesL2Navigation, setHideNavigation);
    }
    if (singleRefLocalheaderTop) {
      offsetTop = singleRefLocalheaderTop?.current?.clientHeight ?? 0;
    }
    globalNavHeight = document?.querySelector('.m-global-nav')?.clientHeight ?? 0;
    window.addEventListener('scroll', pageScroll, { passive: true });

    //script to scroll Accessibility Features and Sustainability link accordian into view - OSD-4801
    const linkArray = [
      { link: '#access', target: '[data-reference-hotel-info="access"]' },
      { link: '#sustainability', target: '[data-reference-amenities="sustainability"]' },
    ];
    const accWrapper = document.querySelector('div[data-id="property-details"]');
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    const propertyAcc = (accWrapper && accWrapper?.querySelectorAll('.property-details-accordion')) ?? [];

    const removeAllActiveAcc = (): void => {
      propertyAcc?.forEach(function (acc) {
        acc.classList.remove('active');
      });
    };

    linkArray.forEach(item => {
      const infoLink = document.querySelectorAll(`a[href='${item.link}']`)[0];
      if (infoLink) {
        infoLink.setAttribute('role', 'button');
        infoLink.setAttribute('href', '#');
        const referenceInfo = document.querySelectorAll(item.target)[0];
        infoLink.addEventListener('click', e => {
          e.preventDefault();
          setTimeout(() => {
            removeAllActiveAcc();
            referenceInfo.classList.add('active');
            accWrapper?.scrollIntoView();
          }, 200);
        });
      }
    });
  }, []);

  const hideFunction = (
    selectedStatesArr: string | string[],
    setStateFunc: {
      (value: React.SetStateAction<boolean>): void;
      (value: React.SetStateAction<boolean>): void;
      (arg0: boolean): void;
    }
  ) => {
    if (preProcessorResponse?.isCNA) {
      if (selectedStatesArr?.includes(cna)) {
        setStateFunc(true);
      }
    }
    if (selectedStatesArr?.includes(preProcessorResponse?.state) || pageProps?.isAuthorMode) {
      setStateFunc(true);
    }
  };

  const pageScroll = (): void => {
    const stickyNav = singleRefLocalheaderTop?.current;
    const globalNav: any = document.querySelector('.m-global-nav');
    const currentScroll = document.documentElement.scrollTop || document.body.scrollTop;
    if (!stickyNav) {
      return;
    }

    if (window.pageYOffset >= offsetTop) {
      singleRefAnchorList.current?.classList.add('sticky');
      if (currentScroll < lastScrollTop) {
        globalNav?.classList.add('sticky');
        singleRefAnchorList?.current?.classList.remove('top-sticky');
        if (globalNav) {
          globalNav.style.top = 0;
        }
      } else {
        globalNav?.classList.add('sticky');
        singleRefAnchorList?.current?.classList.add('top-sticky');
        if (globalNav) {
          if (currentScroll < 60) {
            globalNav.style.top = `-${currentScroll}px`;
          } else {
            globalNav.style.top = `-${globalNavHeight}px`;
          }
        }
      }
    } else {
      singleRefAnchorList?.current?.classList.remove('sticky');
      globalNav?.classList.remove('sticky');
      singleRefAnchorList?.current?.classList.remove('top-sticky');
      if (globalNav) {
        globalNav.style.top = 0;
      }
    }
    lastScrollTop = currentScroll;
    const stickOffset = stickyNav.offsetTop;

    if (window.pageYOffset >= stickOffset) {
      if (!stickyNav.classList.contains('sticky')) {
        stickyNav.classList.add('sticky');
      }
      linkedTargetElements.forEach((linkTarget: any) => {
        if (!linkTarget || !linkTarget.targetElement) {
          return;
        }
        const clientRects = linkTarget.targetElement.getBoundingClientRect();
        if (parseInt(clientRects.top) - 48 <= 0 && parseInt(clientRects.bottom) - 240 >= 0) {
          linkTarget.linkElement.classList.add('active');
          if (linkTarget.linkElement.getBoundingClientRect().right > window.innerWidth) {
            document
              ?.querySelector('.local-header .sticky-nav')
              ?.scrollTo(linkTarget.linkElement.getBoundingClientRect().left, 0);
          }
          linkTarget.linkElement.scrollIntoView();
        } else {
          linkTarget.linkElement.classList.remove('active');
        }
      });
    } else {
      if (stickyNav.classList.contains('sticky')) {
        stickyNav.classList.remove('sticky');
      }
    }
  };

  const linkClicked = (event: { target: { hash: string }; preventDefault: () => void }) => {
    if (event?.target && event?.target?.hash !== '') {
      event.preventDefault();
      const requiredElementHash = event.target.hash.substr(1);
      const requiredElement = document.querySelector(`[id="${requiredElementHash}"]`) as HTMLElement;
      const v1Offset = 0;
      //const localheader = document.querySelector('.local-header');
      // if (localheader.classList.contains('v2-local-header-wrapper')) {
      //   const v1offsetParent = requiredElement.offsetParent as HTMLElement;
      //   v1Offset = v1offsetParent?.offsetTop;
      // }
      const totalOffset = requiredElement?.offsetTop + v1Offset;
      const singleRefLocalheaderTopRef = singleRefLocalheaderTop.current;
      const singleRefAnchorListRef = singleRefAnchorList.current;

      if (window.scrollY < totalOffset) {
        if (
          singleRefLocalheaderTopRef &&
          singleRefLocalheaderTopRef?.getBoundingClientRect().top >= 0 &&
          singleRefAnchorListRef &&
          window.innerWidth >= 768
        ) {
          window.scrollTo(
            0,
            totalOffset - singleRefAnchorListRef?.clientHeight - singleRefLocalheaderTopRef?.clientHeight
          );
        } else if (
          singleRefLocalheaderTopRef &&
          singleRefLocalheaderTopRef.getBoundingClientRect().top >= 0 &&
          singleRefAnchorListRef &&
          window.innerWidth < 768
        ) {
          globalNavHeight = document?.querySelector('.m-global-nav')?.clientHeight ?? 0;
          window.scrollTo(
            0,
            totalOffset -
              singleRefAnchorListRef?.clientHeight -
              singleRefLocalheaderTopRef?.clientHeight +
              globalNavHeight
          );
        } else {
          if (singleRefAnchorListRef) {
            globalNavHeight = document?.querySelector('.m-global-nav')?.clientHeight ?? 0;
            window.scrollTo(0, totalOffset - singleRefAnchorListRef?.clientHeight - globalNavHeight);
          }
        }
      } else {
        if (singleRefAnchorListRef) {
          globalNavHeight = document?.querySelector('.m-global-nav')?.clientHeight ?? 0;
          window.scrollTo(0, totalOffset - singleRefAnchorListRef?.clientHeight - globalNavHeight);
        }
      }
    }
  };

  const onLocalHeaderDataFetched = (data: any) => {
    setLocalHeaderData({ ...data });
  };

  return (
    <StyledLocalHeader data-component-name="o-ppv5-localheader" className="m-container-fullbleed">
      <div className="local-header" id="main">
        <div className="d-flex flex-wrap mx-md-4 px-md-2 mx-0">
          <div className="local-header__menu col-12 p-0">
            {!localHeaderData ? (
              <LocalHeaderData
                isAuthorMode={pageProps?.isAuthorMode}
                onLocalHeaderDataFetched={onLocalHeaderDataFetched}
              />
            ) : null}
            <div
              className="local-header__menu-top d-flex flex-wrap justify-content-between align-items-center mx-3 px-0 mx-md-0  my-0 my-md-1 my-xl-0"
              ref={singleRefLocalheaderTop}
            >
              <LocalHeaderDescription
                {...pageProps}
                hideMap={hideMap}
                data={localHeaderData}
                isAuthorMode={pageProps?.isAuthorMode}
              />
            </div>
            {!hideNavigation && pageProps?.navigationAttributes?.length ? (
              <LocalHeaderNavigation
                {...pageProps}
                localHeaderNavigationRef={singleRefAnchorList}
                hideNavigation={hideNavigation}
                linkClicked={linkClicked}
              />
            ) : null}
          </div>
        </div>
      </div>
    </StyledLocalHeader>
  );
};

export const LocalHeaderConfig = {
  emptyLabel: 'LocalHeader',
  isEmpty: (_props: MappedComponentProperties) => false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/localheader`,
} as Config<MappedComponentProperties>;

export const LocalHeaderEditable = (props: LocalHeaderProps) => (
  <EditableComponent config={LocalHeaderConfig} {...props}>
    <LocalHeader {...props} />
  </EditableComponent>
);
